import { useCallback, useEffect, useState } from 'react';
import { api, AdminMember } from './adminApi';
import { inputCls, btnPrimary, btnGhost, cardCls } from './AdminPanel';

const CARD_KEYS: Array<[string, string]> = [
  ['overall', 'Overall'],
  ['pace', 'Pace'],
  ['shooting', 'Shooting'],
  ['passing', 'Passing'],
  ['dribbling', 'Dribbling'],
  ['defending', 'Defending'],
  ['physical', 'Physical'],
  ['goalkeeping', 'GK'],
];

const STAT_KEYS: Array<[string, string]> = [
  ['matchesPlayed', 'Matches'],
  ['wins', 'Wins'],
  ['draws', 'Draws'],
  ['losses', 'Losses'],
  ['goals', 'Goals'],
  ['assists', 'Assists'],
  ['cleanSheets', 'Clean sheets'],
  ['motmCount', 'MOTM'],
  ['averageRating', 'Avg rating'],
  ['attendancePct', 'Attendance %'],
  ['lateArrivals', 'Late'],
  ['noShows', 'No-shows'],
  ['yellowCards', 'Yellows'],
  ['redCards', 'Reds'],
  ['hatTricks', 'Hat-tricks'],
];

export default function AdminMembers({ onToast }: { onToast: (msg: string) => void }) {
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AdminMember | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api<{ members: AdminMember[] }>('/api/admin/members');
    if (res.ok && res.data) setMembers(res.data.members);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (member: AdminMember, body: Record<string, unknown>) => {
    const res = await api(`/api/admin/members/${member.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    if (res.ok) { onToast(`${member.displayName} updated`); setEditing(null); await load(); }
    else onToast(res.error || 'Update failed');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading members…</p>;

  return (
    <div className={cardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold">Roster ({members.length})</h2>
        <button onClick={load} className={btnGhost}>↻ Refresh</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Player</th><th className="py-2 pr-3">Role</th><th className="py-2 pr-3">Rating</th>
              <th className="py-2 pr-3">Matches</th><th className="py-2 pr-3">Goals</th><th className="py-2 pr-3">Assists</th><th className="py-2 pr-3">Status</th><th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 pr-3"><b>{m.displayName}</b><div className="text-xs text-gray-500">{m.accountEmail || m.id}</div></td>
                <td className="py-2 pr-3">{m.role}</td>
                <td className="py-2 pr-3 font-bold text-emerald-400">{m.footballCard?.overall ?? '—'}</td>
                <td className="py-2 pr-3">{m.stats?.matchesPlayed ?? 0}</td>
                <td className="py-2 pr-3">{m.stats?.goals ?? 0}</td>
                <td className="py-2 pr-3">{m.stats?.assists ?? 0}</td>
                <td className="py-2 pr-3">{m.isActive === false ? <span className="text-red-400">Inactive</span> : m.verificationStatus === 'approved' ? <span className="text-emerald-400">Verified</span> : <span className="text-amber-400">{m.verificationStatus || '—'}</span>}</td>
                <td className="py-2"><button onClick={() => setEditing(m)} className={btnGhost}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <MemberEditor member={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function MemberEditor({ member, onClose, onSave }: { member: AdminMember; onClose: () => void; onSave: (m: AdminMember, body: Record<string, unknown>) => void }) {
  const [card, setCard] = useState<Record<string, number>>({ ...(member.footballCard || {}) });
  const [stats, setStats] = useState<Record<string, number>>({ ...(member.stats || {}) });
  const [role, setRole] = useState(member.role);
  const [verification, setVerification] = useState(member.verificationStatus || 'pending');
  const [wallet, setWallet] = useState(member.walletBalance ?? 0);
  const [active, setActive] = useState(member.isActive !== false);
  const [displayName, setDisplayName] = useState(member.displayName);
  const [nickname, setNickname] = useState(member.nickname || '');
  const [jersey, setJersey] = useState(member.jerseyNumber ?? 0);
  const [position, setPosition] = useState(member.primaryPosition || 'CM');
  const [notes, setNotes] = useState(member.internalNotes || '');

  const num = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => setter(Number(e.target.value));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#12181f] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-black">Edit {member.displayName}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white">×</button>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
          <label className="block"><span className="text-xs text-gray-500">Display name</span><input className={inputCls} value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></label>
          <label className="block"><span className="text-xs text-gray-500">Nickname</span><input className={inputCls} value={nickname} onChange={(e) => setNickname(e.target.value)} /></label>
          <label className="block"><span className="text-xs text-gray-500">Jersey</span><input className={inputCls} type="number" value={jersey} onChange={num(setJersey)} /></label>
          <label className="block"><span className="text-xs text-gray-500">Position</span>
            <select className={inputCls} value={position} onChange={(e) => setPosition(e.target.value)}>
              {['GK','CB','LB','RB','CDM','CM','CAM','LM','RM','LW','RW','ST'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-gray-500">Role</span>
            <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
              {['owner','admin','organizer','player'].map((r) => <option key={r}>{r}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-gray-500">Verification</span>
            <select className={inputCls} value={verification} onChange={(e) => setVerification(e.target.value)}>
              {['approved','pending','rejected'].map((v) => <option key={v}>{v}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-gray-500">Wallet (৳)</span><input className={inputCls} type="number" value={wallet} onChange={num(setWallet)} /></label>
          <label className="flex items-center gap-2 pt-5 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active on roster</label>
        </div>

        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Football card</div>
        <div className="mb-4 grid grid-cols-4 gap-3 md:grid-cols-8">
          {CARD_KEYS.map(([key, label]) => (
            <label key={key} className="block"><span className="text-xs text-gray-500">{label}</span>
              <input className={inputCls} type="number" min={0} max={99} value={card[key] ?? 0} onChange={(e) => setCard({ ...card, [key]: Number(e.target.value) })} />
            </label>
          ))}
        </div>

        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Stats</div>
        <div className="mb-4 grid grid-cols-4 gap-3 md:grid-cols-8">
          {STAT_KEYS.map(([key, label]) => (
            <label key={key} className="block"><span className="text-xs text-gray-500">{label}</span>
              <input className={inputCls} type="number" min={0} value={stats[key] ?? 0} onChange={(e) => setStats({ ...stats, [key]: Number(e.target.value) })} />
            </label>
          ))}
        </div>

        <label className="mb-4 block"><span className="text-xs text-gray-500">Internal notes</span>
          <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={() => onSave(member, {
            displayName, nickname, jerseyNumber: jersey, primaryPosition: position,
            role, verificationStatus: verification, walletBalance: wallet, isActive: active,
            footballCard: card, stats, internalNotes: notes,
          })}>Save changes</button>
        </div>
      </div>
    </div>
  );
}